import { useShadcnNotification } from '@src/components/ui/notification';

import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import ProSpinner from '@shared/newComponents/ProSpinner';
import {
    MobileSignSession,
    MobileSignSessionStatus,
} from '@shared/types/process';
import {
    desktopQrSessionCreate,
    type MobileSignSessionCreateCommand,
} from '@shared/util/signPreparation';

import type { MobileSignUrls, SignFlowTriggerOptions } from './SignFlow';

import { ShadcnButton } from '@shadcn-features/core';
import { QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

const ACCOUNT_MOBILE_SIGN_URLS: MobileSignUrls = {
    create: 'flow/Account/MobileSignSessions/Create',
    status: 'flow/Account/MobileSignSessions/Status/{id}',
    cancel: 'flow/Account/MobileSignSessions/Cancel/{id}',
};

type MobileSignTerminalStatus = 'Expired' | 'Failed' | 'Canceled' | 'Consumed';

export type DesktopQrSignFlowRef<T> = {
    start: (
        data: T,
        options?: SignFlowTriggerOptions,
        signPreparationId?: string | null,
    ) => Promise<void>;
    cancel: () => void;
};

type DesktopQrSignFlowProps<T> = {
    mobileSignServiceId: string;
    mobileSignUrls?: MobileSignUrls;
    visible: boolean;
    onBeforeSign?: () => Promise<void>;
    onAfterSign?: (extraData?: T) => Promise<void>;
    onBack: () => void;
    onClose: () => void;
};

const InnerDesktopQrSignFlow = <T extends object>(
    {
        mobileSignServiceId,
        mobileSignUrls = ACCOUNT_MOBILE_SIGN_URLS,
        visible,
        onBeforeSign,
        onAfterSign,
        onBack,
        onClose,
    }: DesktopQrSignFlowProps<T>,
    ref: React.Ref<DesktopQrSignFlowRef<T>>,
) => {
    const { t } = useTranslation();
    const notification = useShadcnNotification();
    const [loading, setLoading] = useState(false);
    const [session, setSession] = useState<MobileSignSession>();
    const [qrDataUrl, setQrDataUrl] = useState<string>();
    const [expiresAt, setExpiresAt] = useState<string>();
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [terminalStatus, setTerminalStatus] =
        useState<MobileSignTerminalStatus>();
    const dataRef = useRef<T>();
    const optionsRef = useRef<SignFlowTriggerOptions>();
    const preparationIdRef = useRef<string | null>(null);
    const requestIdRef = useRef(0);
    const sessionIdRef = useRef<string>();
    const pollRef = useRef<ReturnType<typeof setInterval>>();
    const statusPendingRef = useRef(false);

    const clearPoll = useCallback(() => {
        if (!pollRef.current) return;
        clearInterval(pollRef.current);
        pollRef.current = undefined;
        statusPendingRef.current = false;
    }, []);

    const cancelMutation = useQueryMutation<string>(
        mobileSignUrls.cancel,
        'post',
        {
            variablesOption(url, variables, axiosConfig) {
                return {
                    url: url.replace('{id}', variables),
                    axiosConfig,
                };
            },
            hideSuccessNotification: true,
        },
    );

    const createMutation = useQueryMutation<
        MobileSignSessionCreateCommand,
        MobileSignSession
    >(mobileSignUrls.create, 'post', {
        hideSuccessNotification: true,
    });

    const statusMutation = useQueryMutation<string, MobileSignSessionStatus>(
        mobileSignUrls.status,
        'get',
        {
            variablesOption(url, variables, axiosConfig) {
                return {
                    url: url.replace('{id}', variables),
                    axiosConfig,
                };
            },
            hideSuccessNotification: true,
        },
    );

    const reset = useCallback(() => {
        requestIdRef.current += 1;
        clearPoll();
        sessionIdRef.current = undefined;
        setSession(undefined);
        setQrDataUrl(undefined);
        setExpiresAt(undefined);
        setRemainingSeconds(0);
        setTerminalStatus(undefined);
        setLoading(false);
    }, [clearPoll]);

    const cancel = useCallback(() => {
        const sessionId = sessionIdRef.current;
        reset();
        if (sessionId) void cancelMutation.mutateAsync(sessionId);
    }, [cancelMutation, reset]);

    const completeSign = useCallback(
        async (cms: string, sessionId: string) => {
            clearPoll();
            sessionIdRef.current = undefined;
            setLoading(true);
            try {
                await onAfterSign?.({
                    signData: cms,
                    mobileSignSessionId: sessionId,
                    ...dataRef.current,
                    buttonProps: null,
                } as T);
                reset();
                onClose();
            } catch (error) {
                setTerminalStatus('Failed');
                notification.error({
                    message: t('translation:signError'),
                    description: t('translation:signErrorDescription'),
                });
                console.error(error, 'desktopMobileSignError');
            } finally {
                setLoading(false);
            }
        },
        [clearPoll, notification, onAfterSign, onClose, reset, t],
    );

    const pollStatus = useCallback(
        async (sessionId: string) => {
            if (statusPendingRef.current) return;
            statusPendingRef.current = true;
            try {
                const status = await statusMutation.mutateAsync(sessionId);
                if (sessionIdRef.current !== sessionId) return;

                setExpiresAt(status.expiresAt);
                if (status.status === 'Completed' && status.cms) {
                    await completeSign(status.cms, sessionId);
                    return;
                }

                if (
                    status.status === 'Expired' ||
                    status.status === 'Failed' ||
                    status.status === 'Canceled' ||
                    status.status === 'Consumed'
                ) {
                    clearPoll();
                    sessionIdRef.current = undefined;
                    setTerminalStatus(status.status);
                }
            } catch (error) {
                if (sessionIdRef.current !== sessionId) return;
                clearPoll();
                sessionIdRef.current = undefined;
                setTerminalStatus('Failed');
                void cancelMutation.mutateAsync(sessionId);
                console.error(error, 'desktopMobileSignStatusError');
            } finally {
                statusPendingRef.current = false;
            }
        },
        [cancelMutation, clearPoll, completeSign, statusMutation],
    );

    const start = useCallback(
        async (
            data: T,
            options?: SignFlowTriggerOptions,
            signPreparationId?: string | null,
        ) => {
            const buttonId = (data as { buttonId?: unknown }).buttonId;
            const signButtonActionId = (
                data as { signButtonActionId?: unknown }
            ).signButtonActionId;
            const intent = options?.mobileSignIntent ?? 'ServiceDocumentSign';
            if (typeof buttonId !== 'string') {
                notification.error({
                    message: t('translation:signError'),
                    description: t('translation:signErrorDescription'),
                });
                onBack();
                return;
            }

            const previousSessionId = sessionIdRef.current;
            const requestId = requestIdRef.current + 1;
            requestIdRef.current = requestId;
            dataRef.current = data;
            optionsRef.current = options;
            preparationIdRef.current = signPreparationId ?? null;
            clearPoll();
            sessionIdRef.current = undefined;
            setLoading(true);
            setSession(undefined);
            setQrDataUrl(undefined);
            setExpiresAt(undefined);
            setRemainingSeconds(0);
            setTerminalStatus(undefined);
            if (previousSessionId) {
                void cancelMutation.mutateAsync(previousSessionId);
            }

            try {
                await onBeforeSign?.();
                const createdSession = await createMutation.mutateAsync(
                    desktopQrSessionCreate({
                        intent,
                        serviceId: mobileSignServiceId,
                        buttonId,
                        signButtonActionId:
                            typeof signButtonActionId === 'string'
                                ? signButtonActionId
                                : null,
                        signPreparationId: preparationIdRef.current,
                    }),
                );
                if (requestId !== requestIdRef.current) {
                    void cancelMutation.mutateAsync(createdSession.sessionId);
                    return;
                }

                sessionIdRef.current = createdSession.sessionId;
                const createdQrDataUrl = await QRCode.toDataURL(
                    createdSession.qrContent,
                    {
                        errorCorrectionLevel: 'M',
                        margin: 2,
                        width: 288,
                    },
                );
                if (requestId !== requestIdRef.current) {
                    void cancelMutation.mutateAsync(createdSession.sessionId);
                    return;
                }

                setSession(createdSession);
                setQrDataUrl(createdQrDataUrl);
                setExpiresAt(createdSession.expiresAt);
                pollRef.current = setInterval(() => {
                    void pollStatus(createdSession.sessionId);
                }, 2500);
                void pollStatus(createdSession.sessionId);
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                const sessionId = sessionIdRef.current;
                clearPoll();
                sessionIdRef.current = undefined;
                setTerminalStatus('Failed');
                if (sessionId) void cancelMutation.mutateAsync(sessionId);
                notification.error({
                    message: t('translation:signError'),
                    description: t('translation:signErrorDescription'),
                });
                console.error(error, 'desktopMobileSignStartError');
            } finally {
                if (requestId === requestIdRef.current) setLoading(false);
            }
        },
        [
            cancelMutation,
            clearPoll,
            createMutation,
            mobileSignServiceId,
            notification,
            onBack,
            onBeforeSign,
            pollStatus,
            t,
        ],
    );

    useImperativeHandle(ref, () => ({ start, cancel }), [cancel, start]);

    useEffect(() => {
        if (!expiresAt) return;

        const updateRemainingSeconds = () => {
            setRemainingSeconds(
                Math.max(
                    0,
                    Math.ceil(
                        (new Date(expiresAt).getTime() - Date.now()) / 1000,
                    ),
                ),
            );
        };

        updateRemainingSeconds();
        const interval = setInterval(updateRemainingSeconds, 1000);
        return () => clearInterval(interval);
    }, [expiresAt]);

    const cancelMutationRef = useRef(cancelMutation.mutateAsync);
    cancelMutationRef.current = cancelMutation.mutateAsync;
    useEffect(
        () => () => {
            const sessionId = sessionIdRef.current;
            clearPoll();
            if (sessionId) void cancelMutationRef.current(sessionId);
        },
        [clearPoll],
    );

    const handleBack = () => {
        cancel();
        onBack();
    };

    const handleClose = () => {
        cancel();
        onClose();
    };

    const handleRestart = () => {
        if (dataRef.current) {
            void start(
                dataRef.current,
                optionsRef.current,
                preparationIdRef.current,
            );
        }
    };

    const terminalStatusText: Record<MobileSignTerminalStatus, string> = {
        Expired: t('translation:mobileSignExpired'),
        Canceled: t('translation:mobileSignCanceled'),
        Failed: t('translation:mobileSignFailed'),
        Consumed: t('translation:mobileSignConsumed'),
    };

    const formattedRemainingTime = `${String(
        Math.floor(remainingSeconds / 60),
    ).padStart(2, '0')}:${String(remainingSeconds % 60).padStart(2, '0')}`;

    return (
        <div
            className={
                visible
                    ? 'flex h-full w-full items-center justify-center overflow-y-auto p-6'
                    : 'hidden'
            }
        >
            <ProSpinner spinning={loading}>
                <div className='bg-background flex min-h-80 w-full max-w-md flex-col items-center gap-4 rounded-xl p-6 text-center shadow-lg'>
                    {terminalStatus ? (
                        <>
                            <QrCode
                                aria-hidden='true'
                                className='text-muted-foreground h-12 w-12'
                            />
                            <h2 className='text-lg font-semibold'>
                                {terminalStatusText[terminalStatus]}
                            </h2>
                            <div className='flex w-full flex-col gap-3 sm:flex-row sm:justify-center'>
                                <ShadcnButton
                                    size='sm'
                                    type='button'
                                    variant='outline'
                                    onClick={handleClose}
                                >
                                    {t('translation:cancel')}
                                </ShadcnButton>
                                <ShadcnButton
                                    size='sm'
                                    type='button'
                                    variant='outline'
                                    onClick={handleBack}
                                >
                                    {t('translation:backToDocument')}
                                </ShadcnButton>
                                {terminalStatus !== 'Consumed' && (
                                    <ShadcnButton
                                        size='sm'
                                        type='button'
                                        variant='brandGreen'
                                        onClick={handleRestart}
                                    >
                                        {t('translation:mobileSignRestart')}
                                    </ShadcnButton>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className='text-lg font-semibold'>
                                {t('translation:mobileSignQrTitle')}
                            </h2>
                            <p className='text-muted-foreground text-sm'>
                                {t('translation:mobileSignQrDescription')}
                            </p>
                            {qrDataUrl && session && (
                                <img
                                    alt={t('translation:mobileSignQrCode')}
                                    className='h-72 w-72 max-w-full rounded-lg bg-white p-2'
                                    height={288}
                                    src={qrDataUrl}
                                    width={288}
                                />
                            )}
                            {session && (
                                <p
                                    aria-live='polite'
                                    className='text-muted-foreground font-mono text-sm tabular-nums'
                                >
                                    {t('translation:mobileSignExpiresIn', {
                                        time: formattedRemainingTime,
                                    })}
                                </p>
                            )}
                            <div className='flex w-full flex-col gap-3 sm:flex-row sm:justify-center'>
                                <ShadcnButton
                                    size='sm'
                                    type='button'
                                    variant='outline'
                                    onClick={handleClose}
                                >
                                    {t('translation:cancel')}
                                </ShadcnButton>
                                <ShadcnButton
                                    size='sm'
                                    type='button'
                                    variant='outline'
                                    onClick={handleBack}
                                >
                                    {t('translation:backToDocument')}
                                </ShadcnButton>
                            </div>
                        </>
                    )}
                </div>
            </ProSpinner>
        </div>
    );
};

export const DesktopQrSignFlow = forwardRef(InnerDesktopQrSignFlow) as <
    T extends object,
>(
    props: DesktopQrSignFlowProps<T> & {
        ref?: React.Ref<DesktopQrSignFlowRef<T>>;
    },
) => ReturnType<typeof InnerDesktopQrSignFlow>;
